import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Message } from 'semantic-ui-react';
import { useUnitConversions } from '../../../providers/UnitConversionsProvider';

const formStyle = {
    field: {
        display: 'flex',
        alignItems: 'center',
    },
    label: {
        fontSize: '1.2rem',
        marginRight: '1rem',
        width: '40%',
    },
    input: {
        width: '100%',
    },
};

export const UnitConversionModal = ({ open, setOpen, type, selectedConversion, onSuccess }) => {
    const [sourceUnit, setSourceUnit] = useState('');
    const [targetUnit, setTargetUnit] = useState('');
    const [formula, setFormula] = useState('');
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);

    const { createUnitConversion, updateUnitConversion } = useUnitConversions();

    useEffect(() => {
        if (type === 'Edit' && selectedConversion) {
            setSourceUnit(selectedConversion.source_unit ?? '');
            setTargetUnit(selectedConversion.target_unit ?? '');
            setFormula(selectedConversion.formula ?? '');
        } else {
            clearForm();
        }
        setError(null);
    }, [selectedConversion, type]);
    
    const validateInput = () => {
        if (!sourceUnit.trim() || !targetUnit.trim() || !formula.trim()) {
            setError('You must complete all fields before saving.');
            return false;
        }
        if (sourceUnit === targetUnit) {
            setError('Source unit and target unit must be different.');
            return false;
        }
        return true;
    }
    
    const clearForm = () => {
        setSourceUnit('');
        setTargetUnit('');
        setFormula('');
    }

    const handleSubmit = async () => {
        setLoading(true);
        
        if (!validateInput()) {
            setLoading(false);
            return;
        }
        
        try {
            setError(null);
            await createUnitConversion({ source_unit: sourceUnit.trim(), target_unit: targetUnit.trim(), formula: formula.trim() });
            setOpen(false);
            clearForm();
            onSuccess?.();
        } catch (e) {
            setError(e?.response?.data || 'An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = async () => {
        setLoading(true);
        
        if (!validateInput()) {
            setLoading(false);
            return;
        }
        
        try {
            setError(null);
            await updateUnitConversion({
                original_source_unit: selectedConversion.source_unit,
                original_target_unit: selectedConversion.target_unit,
                source_unit: sourceUnit.trim(),
                target_unit: targetUnit.trim(),
                formula: formula.trim(),
            });
            setOpen(false);
            onSuccess?.();
        } catch (e) {
            setError(e?.response?.data || 'An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            size="tiny"
            closeIcon={!loading}
            closeOnDimmerClick={!loading}
            open={open}
            onClose={() => setOpen(false)}
            onOpen={() => setOpen(true)}
            style={{ textAlign: 'left' }}
        >
            <Modal.Header>{type === 'Edit' ? 'Edit Unit Conversion' : 'Add Unit Conversion'}</Modal.Header>
            <Modal.Content>
                <Form>
                    <Form.Field style={formStyle.field}>
                        <label style={formStyle.label}>Source Unit</label>
                        <input
                            placeholder="e.g. kWh"
                            style={formStyle.input}
                            value={sourceUnit}
                            onChange={e => setSourceUnit(e.target.value)}
                        />
                    </Form.Field>
                    <Form.Field style={formStyle.field}>
                        <label style={formStyle.label}>Target Unit</label>
                        <input
                            placeholder="e.g. MBtu"
                            style={formStyle.input}
                            value={targetUnit}
                            onChange={e => setTargetUnit(e.target.value)}
                        />
                    </Form.Field>
                    <Form.Field style={formStyle.field}>
                        <label style={formStyle.label}>Formula</label>
                        <input
                            placeholder="e.g. x * 0.003412"
                            style={formStyle.input}
                            value={formula}
                            onChange={e => setFormula(e.target.value)}
                        />
                    </Form.Field>
                </Form>
                {error && <Message error content={error} />}
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={() => setOpen(false)} disabled={loading}>Cancel</Button>
                {type === 'Edit' ? (
                    <Button 
                        className="ui primary button" 
                        onClick={handleEdit} 
                        loading={loading}
                    >Save</Button>
                ) : (
                    <Button 
                        className="ui primary button" 
                        onClick={handleSubmit}
                        loading={loading}
                    >Submit</Button>
                )}
            </Modal.Actions>
        </Modal>
    );
};
